import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChartCard, GroupedBars, HorizontalBars, KpiCard, NoData } from '../components/charts';
import {
  Button,
  EmptyState,
  ErrorNote,
  Loading,
  StatusBadge,
  Tabs,
  compactInputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import {
  DEFAULT_PRESET,
  PERIOD_PRESETS,
  dayLabel,
  formatDuration,
  formatPercent,
  isPreset,
  resolvePeriod,
  type PeriodPreset,
} from '../lib/dashboard-period';
import { formatDate, formatMoney } from '../lib/format';
import {
  activityTypes as activityTypesApi,
  chatApi,
  dashboardApi,
  listAttendants,
  listPipelines,
  type DashboardParams,
} from '../lib/resources';

type Tab = 'negocios' | 'atividades' | 'multiatendimento';

const COLORS = { created: '#6366f1', won: '#10b981', lost: '#ef4444', done: '#0ea5e9' };

/**
 * Início (doc 00 §2): abas Negócios, Atividades e Multiatendimento, com
 * período, filtros e exportação — tudo na URL, para o link abrir o mesmo
 * recorte. Multiatendimento conta os atendimentos do chat (DC3).
 */
export function HomePage() {
  const { session, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const seesBusinesses = can('pipeline_admin', 'pipeline_member');
  const seesAllActivities = can('activities_admin');
  const seesChat = can(
    'multiservice_supervisor',
    'multiservice_attendant_supervisor',
    'multiservice_attendant',
  );

  const requestedTab = params.get('aba') as Tab | null;
  const tab: Tab =
    requestedTab === 'atividades' || requestedTab === 'multiatendimento'
      ? requestedTab
      : seesBusinesses
        ? 'negocios'
        : 'atividades';
  const presetParam = params.get('periodo');
  const preset: PeriodPreset = isPreset(presetParam) ? presetParam : DEFAULT_PRESET;
  const { from, to } = resolvePeriod(preset, params.get('de'), params.get('ate'));

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const businessParams: DashboardParams = {
    from,
    to,
    pipelineId: params.get('pipeline') ?? undefined,
    attendant: params.get('atendente') ?? undefined,
  };
  const activityParams: DashboardParams = {
    from,
    to,
    attendant: seesAllActivities ? (params.get('atendente') ?? undefined) : undefined,
    activityTypeId: params.get('tipo') ?? undefined,
  };

  const pipelines = useQuery({
    queryKey: ['pipelines'],
    queryFn: listPipelines,
    enabled: seesBusinesses,
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const types = useQuery({
    queryKey: ['activity-types'],
    queryFn: () => activityTypesApi.list(),
  });

  const exporter = useMutation({
    mutationFn: () =>
      tab === 'atividades'
        ? dashboardApi.export('activities', activityParams)
        : dashboardApi.export('businesses', businessParams),
  });

  const tabs: Array<[Tab, string]> = [
    ...(seesBusinesses ? ([['negocios', 'Negócios']] as Array<[Tab, string]>) : []),
    ['atividades', 'Atividades'],
    ['multiatendimento', 'Multiatendimento'],
  ];

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Início</h1>
          <p className="text-sm text-slate-500">{session?.workspace.name}</p>
        </div>
        {
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Período"
              className={`${compactInputClass} w-44`}
              value={preset}
              onChange={(event) =>
                update({
                  periodo: event.target.value === DEFAULT_PRESET ? null : event.target.value,
                })
              }
            >
              {(Object.keys(PERIOD_PRESETS) as PeriodPreset[]).map((key) => (
                <option key={key} value={key}>
                  {PERIOD_PRESETS[key]}
                </option>
              ))}
            </select>
            {preset === 'custom' && (
              <>
                <input
                  type="date"
                  aria-label="De"
                  className={`${compactInputClass} w-36`}
                  value={params.get('de') ?? ''}
                  onChange={(event) => update({ de: event.target.value })}
                />
                <span className="text-xs text-slate-400">até</span>
                <input
                  type="date"
                  aria-label="Até"
                  className={`${compactInputClass} w-36`}
                  value={params.get('ate') ?? ''}
                  onChange={(event) => update({ ate: event.target.value })}
                />
              </>
            )}
            {tab === 'negocios' && (
              <select
                aria-label="Pipeline"
                className={`${compactInputClass} w-44`}
                value={params.get('pipeline') ?? ''}
                onChange={(event) => update({ pipeline: event.target.value })}
              >
                <option value="">Todas as pipelines</option>
                {(pipelines.data?.data ?? []).map((pipeline) => (
                  <option key={pipeline.id} value={pipeline.id}>
                    {pipeline.name}
                  </option>
                ))}
              </select>
            )}
            {(tab === 'negocios' || (tab === 'atividades' && seesAllActivities)) && (
              <select
                aria-label="Atendente"
                className={`${compactInputClass} w-44`}
                value={params.get('atendente') ?? ''}
                onChange={(event) => update({ atendente: event.target.value })}
              >
                <option value="">Todos os atendentes</option>
                {(attendants.data?.data ?? []).map((attendant) => (
                  <option key={attendant.id} value={attendant.userId}>
                    {attendant.name}
                  </option>
                ))}
              </select>
            )}
            {tab === 'atividades' && (
              <select
                aria-label="Tipo de atividade"
                className={`${compactInputClass} w-40`}
                value={params.get('tipo') ?? ''}
                onChange={(event) => update({ tipo: event.target.value })}
              >
                <option value="">Todos os tipos</option>
                {(types.data?.data ?? []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            )}
            {tab !== 'multiatendimento' && (
              <Button onClick={() => exporter.mutate()} disabled={exporter.isPending}>
                {exporter.isPending ? 'Exportando…' : 'Exportar'}
              </Button>
            )}
          </div>
        }
      </header>

      <Tabs tabs={tabs} value={tab} onChange={(next) => update({ aba: next })} />
      {exporter.error && (
        <ErrorNote>{errorMessage(exporter.error, 'Falha ao exportar')}</ErrorNote>
      )}

      {tab === 'negocios' && <BusinessTab params={businessParams} />}
      {tab === 'atividades' && (
        <ActivitiesTab params={activityParams} seesAll={seesAllActivities} />
      )}
      {tab === 'multiatendimento' &&
        (seesChat ? (
          <MultiserviceTab from={from} to={to} />
        ) : (
          <EmptyState
            title="Sem acesso ao multiatendimento"
            hint="Os indicadores de atendimento são de quem tem um papel de multiatendimento."
          />
        ))}
    </div>
  );
}

function BusinessTab({ params }: { params: DashboardParams }) {
  const query = useQuery({
    queryKey: ['dashboard', 'businesses', params],
    queryFn: () => dashboardApi.businesses(params),
  });
  if (query.isLoading) return <Loading label="Calculando…" />;
  if (query.isError || !query.data) {
    return <ErrorNote>{errorMessage(query.error, 'Falha ao carregar os números')}</ErrorNote>;
  }
  const data = query.data;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          label="Criados"
          count={data.kpis.created.count}
          value={data.kpis.created.total}
        />
        <KpiCard
          label="Ganhos"
          tone="emerald"
          count={data.kpis.won.count}
          value={data.kpis.won.total}
        />
        <KpiCard
          label="Perdidos"
          tone="red"
          count={data.kpis.lost.count}
          value={data.kpis.lost.total}
        />
        <KpiCard
          label="Em aberto"
          tone="sky"
          count={data.kpis.open.count}
          value={data.kpis.open.total}
          hint="agora, em qualquer data"
        />
        <KpiCard
          label="Total no período"
          count={data.kpis.total.count}
          value={data.kpis.total.total}
          hint="criados, ganhos ou perdidos"
        />
      </div>

      <ChartCard title="Negócios por dia">
        <GroupedBars
          data={data.daily}
          labelKey="date"
          formatLabel={dayLabel}
          series={[
            { key: 'created', label: 'Criados', color: COLORS.created },
            { key: 'won', label: 'Ganhos', color: COLORS.won },
            { key: 'lost', label: 'Perdidos', color: COLORS.lost },
          ]}
        />
      </ChartCard>

      <div className="grid gap-5 lg:grid-cols-2">
        <ChartCard title="Valor ganho por atendente">
          <HorizontalBars
            color={COLORS.won}
            empty="Nenhum negócio ganho no período."
            items={data.byAttendant.map((item) => ({
              key: item.attendantId ?? 'sem',
              label: item.name,
              value: item.total || item.count,
              display: `${formatPercent(item.share)} · ${formatMoney(item.total)}`,
            }))}
          />
        </ChartCard>
        <ChartCard title="Produtos mais vendidos">
          <HorizontalBars
            empty="Nenhum produto em negócios ganhos no período."
            items={data.topProducts.map((item) => ({
              key: item.productId,
              label: item.name,
              value: item.total,
              display: formatMoney(item.total),
              detail: `${item.quantity} un. · ${item.deals} negócio(s)`,
            }))}
          />
        </ChartCard>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <ChartCard title="Ranking de atendentes">
          {data.ranking.length === 0 ? (
            <NoData />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase text-slate-400">
                <tr>
                  <th className="py-1">Atendente</th>
                  <th className="py-1 text-right">Criados</th>
                  <th className="py-1 text-right">Ganhos</th>
                  <th className="py-1 text-right">Valor ganho</th>
                  <th className="py-1 text-right">Perdidos</th>
                  <th className="py-1 text-right">Conversão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.ranking.map((item) => (
                  <tr key={item.attendantId ?? 'sem'}>
                    <td className="py-1.5 text-slate-700">{item.name}</td>
                    <td className="py-1.5 text-right">{item.created}</td>
                    <td className="py-1.5 text-right text-emerald-700">{item.won}</td>
                    <td className="py-1.5 text-right">{formatMoney(item.wonValue)}</td>
                    <td className="py-1.5 text-right text-red-600">{item.lost}</td>
                    <td className="py-1.5 text-right font-medium">
                      {formatPercent(item.conversion)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ChartCard>
        <ChartCard title="Últimos negócios">
          {data.latest.length === 0 ? (
            <NoData text="Nenhum negócio ainda." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.latest.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-2 py-1.5 text-sm"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/negocios/${item.id}`}
                      className="truncate font-medium hover:underline"
                    >
                      {item.leadName}
                    </Link>
                    <p className="text-[11px] text-slate-400">
                      #{item.code} · {item.stageName} · {formatDate(item.createdAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-slate-600">{formatMoney(item.total)}</span>
                    <StatusBadge status={item.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function ActivitiesTab({ params, seesAll }: { params: DashboardParams; seesAll: boolean }) {
  const query = useQuery({
    queryKey: ['dashboard', 'activities', params],
    queryFn: () => dashboardApi.activities(params),
  });
  if (query.isLoading) return <Loading label="Calculando…" />;
  if (query.isError || !query.data) {
    return <ErrorNote>{errorMessage(query.error, 'Falha ao carregar os números')}</ErrorNote>;
  }
  const data = query.data;

  return (
    <div className="space-y-5">
      {!seesAll && <p className="text-xs text-slate-500">Mostrando só as suas atividades.</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Concluídas no período" tone="emerald" count={data.totals.completed} />
        <KpiCard label="Em aberto" tone="sky" count={data.totals.open} hint="agora" />
        <KpiCard
          label="Atrasadas"
          tone="red"
          count={data.totals.overdue}
          hint="abertas e vencidas"
        />
        <KpiCard
          label="Tempo médio até concluir"
          count={formatDuration(data.totals.avgCompletionMs)}
          hint="da criação à conclusão"
        />
      </div>

      <ChartCard title="Atividades concluídas por dia">
        <GroupedBars
          data={data.daily}
          labelKey="date"
          formatLabel={dayLabel}
          series={[{ key: 'completed', label: 'Concluídas', color: COLORS.done }]}
        />
      </ChartCard>

      <div className="grid gap-5 lg:grid-cols-2">
        <ChartCard title="Por atendente">
          <HorizontalBars
            color={COLORS.done}
            empty="Nenhuma atividade."
            items={data.byAttendant.map((item) => ({
              key: item.attendantId ?? 'sem',
              label: item.name,
              value: item.completed,
              display: `${item.completed} concluída(s)`,
              detail: `${item.open} em aberto · ${item.overdue} atrasada(s)`,
            }))}
          />
        </ChartCard>
        <ChartCard title="Por tipo">
          <HorizontalBars
            empty="Nenhuma atividade concluída no período."
            items={data.byType.map((item) => ({
              key: item.activityTypeId ?? 'sem',
              label: item.name,
              value: item.completed,
              display: `${item.completed} concluída(s)`,
            }))}
          />
        </ChartCard>
      </div>
    </div>
  );
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** Início › Multiatendimento (DC3): atendimentos do período e seus tempos. */
function MultiserviceTab({ from, to }: { from: string; to: string }) {
  const query = useQuery({
    queryKey: ['dashboard', 'multiservice', from, to],
    queryFn: () => chatApi.dashboard({ from, to }),
  });
  if (query.isLoading) return <Loading label="Calculando…" />;
  if (query.isError || !query.data) {
    return <ErrorNote>{errorMessage(query.error, 'Falha ao carregar os números')}</ErrorNote>;
  }
  const data = query.data;
  const bars = (
    items: Array<{
      key: string | null;
      name: string;
      count: number;
      avgWaitMs?: number | null;
    }>,
  ) =>
    items.map((item) => ({
      key: item.key ?? 'sem',
      label: item.name,
      value: item.count,
      display: `${item.count}`,
      detail:
        item.avgWaitMs !== undefined
          ? `espera média ${formatDuration(item.avgWaitMs ?? null)}`
          : undefined,
    }));

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard label="Atendimentos" count={data.totals.total} />
        <KpiCard label="Finalizados" tone="emerald" count={data.totals.finished} />
        <KpiCard label="Iniciados" tone="sky" count={data.totals.started} hint="com resposta" />
        <KpiCard
          label="Aguardando"
          tone="amber"
          count={data.totals.waiting}
          hint="agora, sem resposta"
        />
        <KpiCard
          label="Tempo para iniciar"
          count={formatDuration(data.totals.avgWaitMs)}
          hint="no horário de trabalho"
        />
        <KpiCard label="Duração média" count={formatDuration(data.totals.avgDurationMs)} />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <ChartCard title="Por hora do dia">
          <GroupedBars
            data={data.byHour.map((item) => ({
              label: `${String(item.hour).padStart(2, '0')}h`,
              count: item.count,
            }))}
            labelKey="label"
            series={[{ key: 'count', label: 'Atendimentos', color: COLORS.done }]}
          />
        </ChartCard>
        <ChartCard title="Por dia da semana">
          <GroupedBars
            data={data.byWeekday.map((item) => ({
              label: WEEKDAYS[item.weekday],
              count: item.count,
            }))}
            labelKey="label"
            series={[{ key: 'count', label: 'Atendimentos', color: COLORS.created }]}
          />
        </ChartCard>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <ChartCard title="Por departamento">
          <HorizontalBars items={bars(data.byDepartment)} empty="Nenhum atendimento." />
        </ChartCard>
        <ChartCard title="Por atendente">
          <HorizontalBars items={bars(data.byAttendant)} empty="Nenhum atendimento." />
        </ChartCard>
        <ChartCard title="Por conexão">
          <HorizontalBars items={bars(data.byConnection)} empty="Nenhum atendimento." />
        </ChartCard>
      </div>
      <ChartCard title="Atendimentos recentes">
        {data.latest.length === 0 ? (
          <NoData />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase text-slate-400">
              <tr>
                <th className="py-1">Nº</th>
                <th className="py-1">Contato</th>
                <th className="py-1">Departamento</th>
                <th className="py-1">Atendente</th>
                <th className="py-1">Início</th>
                <th className="py-1 text-right">Espera</th>
                <th className="py-1 text-right">Duração</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.latest.map((item) => (
                <tr key={item.id}>
                  <td className="py-1.5">
                    <Link
                      to={`/chat?c=${item.conversationId}`}
                      className="font-medium hover:underline"
                    >
                      #{item.code}
                    </Link>
                  </td>
                  <td className="py-1.5 text-slate-700">{item.contactName}</td>
                  <td className="py-1.5 text-slate-500">{item.departmentName ?? '—'}</td>
                  <td className="py-1.5 text-slate-500">{item.attendantName ?? '—'}</td>
                  <td className="py-1.5 text-slate-500">{formatDate(item.startedAt)}</td>
                  <td className="py-1.5 text-right">{formatDuration(item.waitTimeInMs)}</td>
                  <td className="py-1.5 text-right">
                    {item.finishedAt ? formatDuration(item.durationInMs) : 'em aberto'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ChartCard>
    </div>
  );
}
